import React, { memo, useEffect, useState } from "react";
import mixpanel from "mixpanel-browser";
import { useSelector, useDispatch } from "react-redux";
import ContentContainer from "../../../../templates/content-container/contentContainer";
import ContentContainerToolbar from "../../../../ui-components/molecules/content-container-toolbar/contentContainerToolbar";
import Table from "../../../../ui-components/table/table";
import { AccessorialReviewTerminalPerformanceState } from "../../../../slices/shipment-reconciliation/ShipmentReconciliationState";
import Switch from "react-switch";
import { AccessorialReviewTerminalPerformance, terminalOptions } from "../../../../app/data/shipment-reconciliation/models";
import moment from "moment";
import LabeledDateRangeInput from "../../../../ui-components/molecules/labeled-inputs/labeled-date-range-input/labeledDateRangeInput";
import { LabelModes } from "../../../../ui-components/molecules/labeled-inputs/labeledInput";
import LabeledSelectInput from "../../../../ui-components/molecules/labeled-inputs/labeled-select-input/labeledSelectInput";
import { XGSSelectOption } from "../../../../ui-components/xgs-select/xgsSelect";
import { toast } from "react-toastify";
import "./accessorialsReviewDashboard.scss";
import { accessorialReviewTerminalPerformanceSelector, getAccessorialReviewTerminalPerformance } from "slices/shipment-reconciliation/dashboard/accessorialReviewTerminalPerformanceSlice";
import { getAccessorialReviewTerminalPerformanceColumns } from "./accessorialsReviewTerminalPerformanceColumns";
// import { getAccessorialReviewPerformanceSummary } from "slices/shipment-reconciliation/dashboard/accessorialPerformaceSummarySlice";
// import AccessorialPieChart from "./accessorialPieChart";
// import AccessorialTrendLineChart from "./accessorialTrendLineChart";
// import ChartCard from "../chart-card/chartCard";
// import XGSIcon from "ui-components/icon/xgsIcon";
// import XGSIcons from "ui-components/icon/xgsIcons";
// import Loading from "ui-components/loading/loading";

const AccessorialsReviewDashboard: React.FC<{}> = memo(() => {
  const dispatch = useDispatch();
  const [isAggregateByDate, setIsAggregateByDate] = useState<boolean>(false);
  const [isAggregateByTerminal, setIsAggregateByTerminal] = useState<boolean>(false);
  const [isAggregateByAccessorial, setIsAggregateByAccessorial] = useState<boolean>(false);
  const [dateFrom, setDateFrom] = useState<string>(moment().subtract(2, "weeks").toString());
  const [dateTo, setDateTo] = useState<string>(moment().toString());
  const [terminal, setTerminal] = useState<XGSSelectOption | null | undefined>();

  const accessorialReviewTerminalPerformanceState: AccessorialReviewTerminalPerformanceState = useSelector(
    accessorialReviewTerminalPerformanceSelector
  );

  useEffect(() => {
    mixpanel.track("page_viewed", { page: "shipment_recon_summary" });
  }, [])

  useEffect(() => {
    // dispatch(getAccessorialReviewPerformanceSummary(
    //   {
    //     startDateAdded: (dateFrom) ? dateFrom.toApiDateFormat() : "",
    //     endDateAdded: (dateTo) ? dateTo.toApiDateFormat() : "",
    //     terminalNumber: (terminal && terminal.value) ? +terminal.value : null
    //   },
    //   (error) => { toast.error(error) }
    // ));
    dispatch(getAccessorialReviewTerminalPerformance(
      {
        startDateAdded: (dateFrom) ? dateFrom.toApiDateFormat() : "",
        endDateAdded: (dateTo) ? dateTo.toApiDateFormat() : "",
        terminalNumber: (terminal && terminal.value) ? +terminal.value : null
      },
      (error) => { toast.error(error) }
    ));
  }, [dispatch, dateFrom, dateTo, terminal])

  const onTerminalChanged = (_destination: XGSSelectOption | null | undefined) => {
    setTerminal(_destination);
  };

  const getAccessorialReviewTerminalPerformanceData = (
    data: AccessorialReviewTerminalPerformance[],
    isAggregateByDate: boolean,
    isAggregateByTerminal: boolean,
    isAggregateByAccessorial: boolean
  ) => {
    if (!data || !data.length) return [];
    let removeGroupByFields: string[] = [];
    if (isAggregateByDate) removeGroupByFields.push("dateAdded");
    if (isAggregateByTerminal) removeGroupByFields.push("terminalNumber");
    if (isAggregateByAccessorial) removeGroupByFields.push("accessorialId");
    return reverseGroupBy(
      data,
      ["terminalNumber", "dateAdded", "accessorialId"],
      removeGroupByFields,
      ["operationsPending", "operationsAccepted", "operationsRejected", "billingPending", "billingAccepted", "billingRejected", "acceptedAmount"],
      ["terminalName", "terminalAirportcode", "accessorialName"]
    )
  }

  function reverseGroupBy(
    data: any[],
    currentGroupByFields: string[],
    removeFields: string[],
    sumFields: string[],
    firstInstanceFields: string[]
  ) {
    const newGroupByFields = currentGroupByFields.filter(f => !removeFields.includes(f));

    if (newGroupByFields.length === 0) {
      // No groupBy fields left, return a single total row
      const total: any = {};
      for (const f of sumFields) {
        total[f] = data.reduce((acc, item) => acc + Number(item[f]), 0);
      }

      if (data.length > 0) {
        for (const f of firstInstanceFields) {
          total[f] = data[0][f];
        }
      }

      return [total];
    }

    // group by remaining fields
    const grouped = new Map<string, any>();

    for (const item of data) {
      const key = newGroupByFields.map(f => item[f]).join('|');

      if (!grouped.has(key)) {
        const group: any = {};
        for (const f of newGroupByFields) {
          group[f] = item[f];
        }

        for (const f of sumFields) {
          group[f] = Number(item[f]);
        }

        for (const f of firstInstanceFields) {
          group[f] = item[f];
        }

        grouped.set(key, group);
      } else {
        const group = grouped.get(key)!;
        for (const f of sumFields) {
          group[f] += Number(item[f]);
        }
      }
    }

    return Array.from(grouped.values());
  }

  return (
    <ContentContainer
      className="xgs-list xgs-list--full-width"
      mods={{ 'full-width': true }}
      titleComponent={
        <ContentContainerToolbar
          title="Accessorial Review Dashboard"
          mods={{ "full-width": true }}
        >
        </ContentContainerToolbar>
      }
    >
      <div className="xgs-accessorial-review-dashboard">
        <div className="xgs-accessorial-review-dashboard__controls">
          <div className="xgs-accessorial-review-dashboard__controls__search">
            <LabeledDateRangeInput
              label="Date Added"
              labelMode={LabelModes.column}
              className="xgs-accessorial-review-dashboard__controls__search__input"
              start={dateFrom}
              end={dateTo}
              onStartChange={(v) => {
                setDateFrom(v);
              }}
              onEndChange={(v) => {
                setDateTo(v);
              }}
            />
            <div className="xgs-accessorial-review-dashboard__controls__search__input">
              <LabeledSelectInput
                onValueChange={onTerminalChanged}
                value={terminal}
                options={terminalOptions}
                label="Terminal:"
                labelMode={LabelModes.column}
                className="xgs-tracking-filter__input"
                isClearable
              />
            </div>
          </div>
          <div className="xgs-accessorial-review-dashboard__controls__aggregation">
            <div className="xgs-accessorial-review-dashboard__controls__aggregation__heading">
              Aggregate By:
            </div>
            <div className="xgs-accessorial-review-dashboard__controls__aggregation__field">
              Date:
              <Switch
                onChange={(checked) => setIsAggregateByDate(checked)}
                checked={isAggregateByDate}
                disabled={false}
                width={32}
                height={20}
                offColor="#BDBDBD"
                onColor="#2F80ED"
                uncheckedIcon={<></>}
                checkedIcon={<></>}
              />
            </div>
            <div className="xgs-accessorial-review-dashboard__controls__aggregation__field">
              Terminal:
              <Switch
                onChange={(checked) => setIsAggregateByTerminal(checked)}
                checked={isAggregateByTerminal}
                disabled={false}
                width={32}
                height={20}
                offColor="#BDBDBD"
                onColor="#2F80ED"
                uncheckedIcon={<></>}
                checkedIcon={<></>}
              />
            </div>
            <div className="xgs-accessorial-review-dashboard__controls__aggregation__field">
              Accessorial:
              <Switch
                onChange={(checked) => setIsAggregateByAccessorial(checked)}
                checked={isAggregateByAccessorial}
                disabled={false}
                width={32}
                height={20}
                offColor="#BDBDBD"
                onColor="#2F80ED"
                uncheckedIcon={<></>}
                checkedIcon={<></>}
              />
            </div>
          </div>
        </div>
        <div className="xgs-accessorial-review-dashboard__table-wrapper">
          <Table
            isLoading={accessorialReviewTerminalPerformanceState.loading}
            columns={getAccessorialReviewTerminalPerformanceColumns(isAggregateByDate, isAggregateByTerminal, isAggregateByAccessorial)}
            data={getAccessorialReviewTerminalPerformanceData(accessorialReviewTerminalPerformanceState.results, isAggregateByDate, isAggregateByTerminal, isAggregateByAccessorial)}
            ignoreCellClick="selection"
            cursorPointer={true}
            noResultsText="No data available"
            onSelectedRowsChange={null}
            infiniteScroll={true}
            responsive={true}
            sorting={true}
            enableMultiSort={true}
            minTableHeight={200}
            autoResetSelectedRows={true}
          />
        </div>
        {/* <ChartCard
          title="Accessorial Revenue Breakdown"
          icon={<XGSIcon icon={XGSIcons.faChartPie} />}
          initiallyCollapsed={false}
        >{
            (accessorialReviewTerminalPerformanceState.loading) ? <Loading isLoading={true}></Loading>
              :
              <AccessorialPieChart
                accessorialsPerformance={getAccessorialReviewTerminalPerformanceData(accessorialReviewTerminalPerformanceState.results, true, true, false)}
              />
          }
        </ChartCard>
        <ChartCard
          title="Accessorial Revenue Breakdown"
          icon={<XGSIcon icon={XGSIcons.faChartLine} />}
          initiallyCollapsed={false}
        >{
            (accessorialReviewTerminalPerformanceState.loading) ? <Loading isLoading={true}></Loading>
              :
              <AccessorialTrendLineChart
                accessorialsPerformance={getAccessorialReviewTerminalPerformanceData(accessorialReviewTerminalPerformanceState.results, false, true, true)}
              />
          }
        </ChartCard> */}
      </div>
    </ContentContainer >
  );
});

export default AccessorialsReviewDashboard;